import { useState, useContext, useEffect, useRef } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import { UserContext } from "../UserContext";
import { jwtDecode } from "jwt-decode";
import apiClient from "../../api/client";
import { ASSET_BASE_URL } from "../../config/env";
import { getErrorMessage, isNetworkError } from "../../utils/network";
import './legacy.css';
import './index.css';

const SUPPORT_PHONE = '+91 91252 53535';
const SUPPORT_TEL = '+919125253535';
const REMEMBER_KEY = 'vrplay_last_username';

// Only the username is remembered (never the password), and only on this device.
const readRemembered = () => {
  try { return localStorage.getItem(REMEMBER_KEY) || ''; } catch { return ''; }
};
const writeRemembered = (value) => {
  try {
    if (value) localStorage.setItem(REMEMBER_KEY, value);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch { /* storage blocked — ignore */ }
};

const dashboardFor = (role) =>
  role === 'superadmin' ? '/superadmin' : role === 'admin' ? '/admin' : role === 'operator' ? '/operator' : null;

/** Turns the server's reply into a sentence that says what to do next. */
const loginErrorMessage = (err) => {
  if (isNetworkError(err)) return getErrorMessage(err);
  const status = err?.response?.status;
  const serverMsg = err?.response?.data?.message;
  if (status === 401) return "Username or password is incorrect. Check both and try again.";
  if (status === 403) return serverMsg || "This account has been disabled. Contact your admin.";
  if (status === 429) return serverMsg || "Too many attempts. Wait a few minutes, then try again.";
  return serverMsg || "Couldn't log in right now. Please try again.";
};

const LoginForm = () => {
  const remembered = readRemembered();
  const [username, setUsername] = useState(remembered);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(!!remembered);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  const { user, login } = useContext(UserContext);
  const navigate = useNavigate();

  // Put the cursor where the person needs to type first.
  useEffect(() => {
    (remembered ? passwordRef : usernameRef).current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmitSuccess = (data) => {
    login(data.token);
    writeRemembered(rememberMe ? username.trim() : '');

    const decoded = jwtDecode(data.token);
    const role = decoded.user?.role || data.role;

    // Forced password change (new account, or a superadmin-triggered reset)
    // takes priority over the normal role redirect.
    if (decoded.user?.mustChangePassword) {
      navigate('/change-password');
      return;
    }
    navigate(dashboardFor(role) || '/login');
  };

  const submitForm = async (event) => {
    event.preventDefault();
    const name = username.trim();

    if (!name || !password) {
      setErrorMsg(!name ? 'Enter your username.' : 'Enter your password.');
      (!name ? usernameRef : passwordRef).current?.focus();
      return;
    }

    setIsLoading(true);
    setErrorMsg('');
    try {
      const { data } = await apiClient.post('/login.php', { loginUsername: name, loginPassword: password });
      if (data.token) {
        onSubmitSuccess(data);
        return;
      }
      setErrorMsg(data.message || "Couldn't log in right now. Please try again.");
    } catch (error) {
      setErrorMsg(loginErrorMessage(error));
      if (error?.response?.status === 401) {
        setPassword('');
        passwordRef.current?.focus();
      }
    }
    setIsLoading(false);
  };

  const onPasswordKey = (e) => {
    if (typeof e.getModifierState === 'function') setCapsLock(e.getModifierState('CapsLock'));
  };

  // Already logged in → straight to the right dashboard.
  if (user && dashboardFor(user.role)) {
    return <Navigate to={dashboardFor(user.role)} replace />;
  }

  return (
    <div className="vrl">
      {/* Brand panel — hidden on phones */}
      <aside className="vrl-brand" aria-hidden="true">
        <div className="vrl-brand__inner">
          <p className="vrl-brand__eyebrow">VRPLAY partner portal</p>
          <h2>Manage your customers, renewals and wallet in one place.</h2>
          <ul>
            <li>See who expires today, tomorrow and this week</li>
            <li>Recharge TV, OTT and broadband customers</li>
            <li>Track every wallet top-up and recharge</li>
          </ul>
        </div>
        <svg className="vrl-cable" viewBox="0 0 600 40" preserveAspectRatio="none">
          <path d="M0 20 H520" stroke="#e30d0d" strokeWidth="16" strokeLinecap="round" fill="none" />
          <path d="M518 15 C548 11 572 8 598 4" stroke="#1f2a44" strokeWidth="3" fill="none" />
          <path d="M518 19 C552 18 576 17 598 16" stroke="#8dc63f" strokeWidth="3" fill="none" />
          <path d="M518 23 C552 25 576 27 598 29" stroke="#9aa39a" strokeWidth="3" fill="none" />
          <path d="M518 27 C548 31 570 35 592 38" stroke="#1a9a3a" strokeWidth="3" fill="none" />
        </svg>
      </aside>

      <main className="vrl-main">

        <div className="vrl-card">
          <img src={`${ASSET_BASE_URL}/vrplay1.png`} className="vrl-logo" alt="VRPLAY" />

          <h1>Log in</h1>

          <form onSubmit={submitForm} noValidate>
            <div className="vrl-field">
              <label htmlFor="username">Username</label>
              <input
                ref={usernameRef}
                id="username"
                name="username"
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setErrorMsg(''); }}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                aria-invalid={!!errorMsg && !username.trim()}
              />
            </div>

            <div className="vrl-field">
              <label htmlFor="password">Password</label>
              <div className="vrl-password">
                <input
                  ref={passwordRef}
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrorMsg(''); }}
                  onKeyUp={onPasswordKey}
                  onKeyDown={onPasswordKey}
                  onBlur={() => setCapsLock(false)}
                  autoComplete="current-password"
                  aria-describedby={capsLock ? 'caps-warning' : undefined}
                />
                <button
                  type="button"
                  className="vrl-eye"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {capsLock && <p id="caps-warning" className="vrl-hint">Caps Lock is on.</p>}
            </div>

            {errorMsg && (
              <div className="vrl-error" role="alert">
                <FiAlertCircle aria-hidden="true" />
                <span>{errorMsg}</span>
              </div>
            )}

            <button type="submit" className="vrl-submit" disabled={isLoading} style={{ position: 'relative', marginTop: '0.5rem' }}>
              {isLoading ? <><span className="vrl-spinner" aria-hidden="true" /> Logging in…</> : 'Log in'}
            </button>
          </form>

          {/* <p className="vrl-help">
            Forgot your password? Ask your admin to reset it, or call{' '}
            <a href={`tel:${SUPPORT_TEL}`}>{SUPPORT_PHONE}</a>.
          </p> */}
        </div>

        {/* <p className="vrl-customers">
          Watching VRPLAY at home? You don't need to log in here — use the VRPLAY app on your TV or phone.
        </p> */}
      </main>
    </div>
  );
};

export default LoginForm;
